import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { IconShoppingCartPlus, IconChevronDown, IconCheck } from "@tabler/icons-react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardTitle,
  Button,
  Badge,
  Avatar,
  AvatarFallback,
  cn,
  WorkoutIcon,
} from "@lemonadetraining/web-ui";
import type { LandingLinkedProgramme } from "@lemonadetraining/contracts";
import { AuthContext, cartActions, registerFreeProgram } from "@lemonadetraining/web-shared";
import { describeCommitmentOption } from "@lemonadetraining/web-shared/utils/membershipLabels";
import {
  COMMITMENT_STOREFRONT_COPY,
  activePlanEntries,
  buildMembershipCartLine,
  hasWithdrawnPlansOnly,
  optionInstalment,
} from "@lemonadetraining/web-shared/utils/commitmentPlans";
import * as PRICING from "@lemonadetraining/web-shared/constants/Pricing";
import * as PRODUCTS from "@lemonadetraining/web-shared/constants/ProductConst";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import EffortIndicator from "./EffortIndicator";
import PlanSelector, { type PricingOption } from "./PlanSelector";
import VariantAvatarImage from "./VariantAvatarImage";
import VariantImg from "./VariantImg";

export interface ProductCardProps {
  id: string;
  data: {
    name: string;
    description?: string;
    // Optional so preview and partially-loaded call sites (e.g. the cover-art
    // picker) can render before an owner is known; the coach row falls back to
    // a "?" avatar and the owner label is simply omitted.
    ownerName?: string;
    ownerUID?: string;
    // Nullable so the cover-art picker can pass an explicit "no image yet".
    coverArtURL?: string | null;
    pricing?: {
      type: string;
      amount?: number;
    } | null;
    priceID?: string;
    pricingOptions?: Record<string, PricingOption>;
    membershipType?: string;
    stripeAccountID?: string | null;
    active?: boolean;
    productType?: string;
    isLemonadeProduct?: boolean;
    effortLevel?: number | null;
    linkedProgramming?: { name: string } | null;
    linkedProgrammings?: Pick<LandingLinkedProgramme, "name">[] | null;
    boxID?: string;
  };
  index?: number;
  isDesktop?: boolean;
  onChoosePlan?: (productId: string) => void;
  brandColor?: string;
  /**
   * Called with the product id when the card body is opened. Passing onOpen is
   * what makes the card a navigable detail link; omit it for inert cards
   * (memberships, previews). The caller owns navigation — the dashboard routes
   * in-app, the marketing site hands off to the app domain with an auth token —
   * so this shared component carries no cross-domain logic.
   */
  onOpen?: (productId: string) => void;
  /**
   * Whether to render the add-to-cart / choose-plan action. False for read-only
   * surfaces — the cover-art preview and disabled invitation/library views.
   */
  showAction?: boolean;
}

// The array wins whenever present; the single field is what an older landing response sends.
function linkedProgrammeNames(data: ProductCardProps["data"]): string[] {
  const list = Array.isArray(data.linkedProgrammings)
    ? data.linkedProgrammings
    : data.linkedProgramming
      ? [data.linkedProgramming]
      : [];
  return list
    .map((programme) => (typeof programme?.name === "string" ? programme.name.trim() : ""))
    .filter(Boolean);
}

function ProductCard({
  id,
  data,
  index = 0,
  isDesktop = false,
  onChoosePlan,
  brandColor,
  onOpen,
  showAction = true,
}: ProductCardProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useAppSelector((state) => state.user);
  const authCtx = useContext(AuthContext);
  const shouldReduceMotion = useReducedMotion();

  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [addedState, setAddedState] = useState(false);
  const [loading, setLoading] = useState(false);
  const [planSelectorOpen, setPlanSelectorOpen] = useState(false);
  const successTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Clean up timer on unmount
  useEffect(() => () => clearTimeout(successTimerRef.current), []);

  // Load avatar URL
  useEffect(() => {
    if (data.ownerUID && authCtx?.getProfilePicURL) {
      authCtx
        .getProfilePicURL(data.ownerUID)
        .then((url: string | null) => {
          if (url) setAvatarUrl(url);
        })
        .catch(() => {
          // Avatar is optional; a load failure just leaves the fallback initials.
        });
    }
  }, [data.ownerUID, authCtx]);

  // Pricing logic — only count active options
  const activeOptions = activePlanEntries(data.pricingOptions);

  const hasPricingOptions = activeOptions.length > 0;
  const hasMultiplePlans = activeOptions.length > 1;
  const hasSingleOption = activeOptions.length === 1;

  // A membership whose every pricing option has been withdrawn is not for
  // sale, and must not fall through to the top-level price — that field is a
  // leftover from before the option map existed, and selling at it would take
  // money at a price the gym has stopped honouring. A product that never had
  // an option map is a different thing entirely: it is grandfathered, its
  // top-level price IS its price, and it goes on selling as it always has.
  const isWithdrawn = hasWithdrawnPlansOnly(data.pricingOptions);

  const isFree = !hasPricingOptions && !isWithdrawn && data.pricing?.type === PRICING.TYPE.free;
  const programmes = linkedProgrammeNames(data);

  // Compute pricing label
  let pricingLabel = "";
  if (isWithdrawn) {
    pricingLabel = COMMITMENT_STOREFRONT_COPY.unavailable;
  } else if (hasPricingOptions) {
    if (hasSingleOption) {
      const singleOption = activeOptions[0][1];
      if (singleOption.interval === "month") {
        const { isCommitment, count, unitLabel, totalAmount } =
          describeCommitmentOption(singleOption);
        if (isCommitment) {
          // The card shows what will be charged, with the term named beside it:
          // "EUR 45.00/mo · 12-month term". Quoting the €540 term total here
          // would read as the price of joining, and the shopper would meet the
          // real €45 charge for the first time at checkout.
          const { amount, perPeriodSuffix, termQualifier } = optionInstalment(singleOption);
          pricingLabel = `EUR ${amount.toFixed(2)}${perPeriodSuffix} · ${termQualifier}`;
        } else {
          // A plain option's amount is the FULL billing-period price. One month
          // reads naturally as a monthly rate; a multi-month plan must name its
          // period ("EUR 30.00/3 months"), because labelling a period total
          // "/mo" would overstate the real monthly rate by the interval count.
          pricingLabel =
            count === 1
              ? `EUR ${totalAmount.toFixed(2)}/mo`
              : `EUR ${totalAmount.toFixed(2)}/${unitLabel}`;
        }
      } else {
        pricingLabel = `EUR ${singleOption.amount.toFixed(2)}`;
      }
    } else if (hasMultiplePlans) {
      // Visit cards are priced per visit; recurring per month. A product's options
      // are homogeneous, so the first option's kind labels the whole "From …" range.
      const described = activeOptions.map(([, opt]) => describeCommitmentOption(opt));
      const min = Math.min(...described.map((d) => d.perUnitAmount));
      pricingLabel = `From EUR ${min.toFixed(2)}/${described[0].isVisitCard ? "visit" : "mo"}`;
    }
  } else if (data.pricing) {
    switch (data.pricing.type) {
      case PRICING.TYPE.free:
        pricingLabel = "FREE";
        break;
      case PRICING.TYPE.oneTime:
        pricingLabel = `EUR ${(data.pricing.amount ?? 0).toFixed(2)}`;
        break;
      case PRICING.TYPE.subscription:
        pricingLabel = `EUR ${(data.pricing.amount ?? 0).toFixed(2)}/mo`;
        break;
      default:
        pricingLabel = "";
        break;
    }
  }

  // Action button label
  let buttonLabel: string;
  let ButtonIcon: React.ElementType | null;

  if (hasMultiplePlans) {
    buttonLabel = "Choose Plan";
    ButtonIcon = IconChevronDown;
  } else if (isFree) {
    buttonLabel = "Add to Library";
    ButtonIcon = null;
  } else {
    buttonLabel = "Add to Cart";
    ButtonIcon = IconShoppingCartPlus;
  }

  const showSuccessState = useCallback(() => {
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    setAddedState(true);
    successTimerRef.current = setTimeout(() => setAddedState(false), 1500);
  }, []);

  const handleAction = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (!user.isLoggedIn) {
        toast("Sign in to add items to your cart");
        sessionStorage.setItem("lemonade_landing_auth_redirect", "true");
        navigate("/login", { state: { from: pathname } });
        return;
      }

      if (hasMultiplePlans) {
        if (onChoosePlan) {
          onChoosePlan(id);
        } else {
          setPlanSelectorOpen(true);
        }
        return;
      }

      if (isFree) {
        // A logged-in user always has a uid; guard defensively so the typed
        // FirebaseUser | null narrows and we never register against an empty id.
        if (!user.user?.uid) return;
        setLoading(true);
        registerFreeProgram(user.user.uid, id, data.name)
          .then(() => {
            toast.success("Added to your library!");
            showSuccessState();
          })
          .catch((err: { code?: string; message?: string } | null) => {
            // A refusal carries a sentence the server wrote for the reader; retrying cannot help.
            const refused =
              err?.code === "functions/permission-denied" || err?.code === "permission-denied";
            // The SDK sets the bare code as the message when the server wrote none.
            const serverWrote = !!err?.message && err.message !== "permission-denied";
            toast.error(
              refused && serverWrote && err?.message
                ? err.message
                : "Couldn't add to cart. Please try again."
            );
          })
          .finally(() => {
            setLoading(false);
          });
        return;
      }

      // Add to cart: single pricing or single active pricingOption
      if (hasPricingOptions && hasSingleOption) {
        // A gym selling exactly one plan never opens the popover, so this line
        // goes through the same constructor — otherwise that one purchase would
        // reach checkout looking like a plain membership.
        const [optionKey, option] = activeOptions[0];
        dispatch(
          cartActions.addToCart(
            buildMembershipCartLine(
              {
                productId: id,
                name: data.name,
                coachName: data.ownerName,
                coachUID: data.ownerUID,
                coverArt: data.coverArtURL,
                stripeAccountID: data.stripeAccountID,
                isLemonadeProduct: data.isLemonadeProduct,
                /* Same rule as the legacy branch below: only a membership may
                   carry a gym. Reachable only for a product with a plan matrix,
                   which today means a membership — stated locally rather than
                   resting on that remaining true. */
                boxID: data.productType === PRODUCTS.TYPE.Membership ? data.boxID : undefined,
              },
              option,
              optionKey
            )
          )
        );
      } else {
        dispatch(
          cartActions.addToCart({
            title: data.name,
            programUID: id,
            coachName: data.ownerName,
            coachUID: data.ownerUID,
            coverArt: data.coverArtURL,
            pricing: data.pricing,
            priceID: data.priceID,
            stripeAccountID: data.stripeAccountID || null,
            isLemonadeProduct: data.isLemonadeProduct || false,
            /* The gym, and nothing more: absence of an option map is what marks
               this line legacy, so it gains no commitment fields. Without the
               gym id checkout cannot match a terms refusal to its cart line —
               but ONLY a membership may carry one. The checkout gates classify
               on MembershipProducts, so a gym's programming or coaching product
               presenting as a gym line would have the client demand an
               agreement the server records nowhere. The server reads this from
               a DIFFERENT document — `Products/{uid}.metadata.productType` —
               so the two must stay in step; they agree today because one create
               populates both and no write path strips either. */
            boxID: data.productType === PRODUCTS.TYPE.Membership ? data.boxID : undefined,
          })
        );
      }

      toast.success("Added to cart!");
      showSuccessState();
    },
    [
      user,
      navigate,
      pathname,
      hasMultiplePlans,
      isFree,
      hasPricingOptions,
      hasSingleOption,
      activeOptions,
      id,
      data,
      dispatch,
      onChoosePlan,
      showSuccessState,
    ]
  );

  const initials = data.ownerName
    ? data.ownerName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?";

  const ariaLabel = `View details for ${data.name} by ${data.ownerName}, ${
    pricingLabel || "no price"
  }`;

  // Descriptive aria-label for action button (WCAG)
  let actionAriaLabel: string;
  if (hasMultiplePlans) {
    actionAriaLabel = `Choose a plan for ${data.name}`;
  } else if (isFree) {
    actionAriaLabel = `Add ${data.name} to library`;
  } else {
    actionAriaLabel = `Add ${data.name} to cart, ${pricingLabel}`;
  }

  // Navigability is derived from onOpen, not a separate prop: a card is a detail
  // link only when a caller gave it something to open. Membership and preview
  // cards omit onOpen and stay inert — a membership id is not a workout-programme
  // id, and a preview has nothing to open. Deriving keeps "inert" the safe
  // default, so a card can never be an interactive <a href="#"> that no-ops
  // behind an aria-label promising navigation.
  const linkToDetail = onOpen != null;
  const BodyTag: React.ElementType = linkToDetail ? "a" : "div";
  const bodyProps = linkToDetail
    ? {
        href: "#",
        onClick: (e: React.MouseEvent) => {
          e.preventDefault();
          onOpen?.(id);
        },
        "aria-label": ariaLabel,
        className:
          "focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none rounded-xl",
        "data-testid": `product-card-link-${id}`,
      }
    : { "data-testid": `product-card-body-${id}` };

  return (
    <motion.div
      // The card owns its size: parents range from landing-page grid columns to
      // shrink-to-fit flex rows and dialogs, where content-driven sizing would
      // let the cover image's natural pixel size (or, with no image, the text)
      // dictate the width. Definite 350px either way; max-w-full yields to
      // genuinely narrower containers (grid columns, phone screens).
      className="w-[350px] max-w-full"
      initial={shouldReduceMotion ? false : { opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      whileHover={
        !shouldReduceMotion && isDesktop
          ? { scale: 1.02, transition: { duration: 0.2, delay: 0 } }
          : undefined
      }
      transition={{
        duration: shouldReduceMotion ? 0 : 0.4,
        delay: shouldReduceMotion ? 0 : Math.min(index * 0.15, 0.6),
      }}
    >
      <Card
        role="article"
        data-testid={`product-card-${id}`}
        className={cn(
          "group/product-card h-full transition-shadow pt-0",
          linkToDetail && "cursor-pointer",
          "hover:shadow-lg",
          "focus-within:ring-2 focus-within:ring-ring"
        )}
      >
        {/* Card body — the navigable detail link (BodyTag/bodyProps above), or an
            inert container when the card has no onOpen. */}
        <BodyTag {...bodyProps}>
          {/* Cover image */}
          <div className="relative aspect-[4/3] overflow-hidden rounded-t-xl">
            {data.coverArtURL && !imageError ? (
              <VariantImg
                src={data.coverArtURL}
                size="512x512"
                surface="coverArt"
                alt={data.name}
                loading="lazy"
                onError={() => setImageError(true)}
                className="h-full w-full object-cover transition-opacity duration-500"
                style={{ opacity: 1 }}
                data-testid={`product-card-image-${id}`}
              />
            ) : (
              <div
                className="flex h-full w-full items-center justify-center bg-gradient-to-br from-emerald-600 via-teal-500 to-cyan-400"
                data-testid={`product-card-fallback-${id}`}
              >
                <WorkoutIcon size={48} className="text-white/60" />
              </div>
            )}

            {/* EffortIndicator overlay */}
            {data.effortLevel != null && (
              <div className="absolute top-2 left-2 z-10">
                <EffortIndicator level={data.effortLevel} />
              </div>
            )}
          </div>

          <CardContent className="pt-3">
            {/* Coach row */}
            <div className="mb-2 flex items-center gap-2">
              <Avatar size="sm">
                <VariantAvatarImage src={avatarUrl} alt={data.ownerName} />
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              <span
                className="text-muted-foreground text-xs truncate"
                data-testid={`product-card-coach-${id}`}
              >
                {data.ownerName}
              </span>
            </div>

            {/* Product name */}
            <CardTitle>
              <h3
                className="text-base font-medium leading-snug line-clamp-2"
                data-testid={`product-card-name-${id}`}
              >
                {data.name}
              </h3>
            </CardTitle>

            {programmes.length > 0 && (
              <p
                className="mt-1 text-sm text-card-foreground break-words"
                data-testid={`product-card-programming-${id}`}
              >
                Includes the {programmes[0]} programme
              </p>
            )}
            {programmes.length > 1 && (
              <p
                className="text-xs text-muted-foreground break-words"
                data-testid={`product-card-programming-more-${id}`}
              >
                Also includes: {programmes.slice(1).join(", ")}
              </p>
            )}

            {/* Pricing */}
            <div className="mt-2" data-testid={`product-card-pricing-${id}`}>
              {isFree ? (
                <Badge data-testid={`product-card-free-badge-${id}`}>FREE</Badge>
              ) : (
                <span className="text-sm font-semibold">{pricingLabel}</span>
              )}
            </div>
          </CardContent>
        </BodyTag>

        {/* Nothing on sale — say so plainly and in the shopper's own terms,
            where the buy button would otherwise be. A disabled button here
            would look like something they had done wrong. */}
        {showAction && isWithdrawn && (
          <CardContent className="mt-auto pt-0">
            <p
              className="text-muted-foreground text-center text-xs"
              data-testid={`product-card-unavailable-${id}`}
            >
              {COMMITMENT_STOREFRONT_COPY.unavailableHint}
            </p>
          </CardContent>
        )}

        {/* Action button — outside the card body so it never triggers card navigation.
            mt-auto keeps buttons level across a grid row whose cards differ in height. */}
        {showAction && !isWithdrawn && (
          <CardContent className="mt-auto pt-0">
            <Button
              className="w-full min-h-[44px] focus-visible:ring-2 focus-visible:ring-ring"
              // When a brand accent is in play (marketing landing pages set the
              // --brand-accent custom property and pass brandColor), tint the
              // button; elsewhere (the dashboard, non-branded pages) fall back to
              // the default Button styling so it stays readable and WCAG AA — the
              // brand fallback color is Lemonade yellow, which fails on white text.
              style={
                brandColor ? { backgroundColor: "var(--brand-accent)", color: "white" } : undefined
              }
              onClick={handleAction}
              disabled={loading}
              aria-label={actionAriaLabel}
              data-testid={`add-to-cart-${id}`}
            >
              <AnimatePresence mode="wait" initial={false}>
                {addedState ? (
                  <motion.span
                    key="added"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="flex items-center gap-2"
                    data-testid={`product-card-added-${id}`}
                  >
                    <IconCheck size={18} />
                    Added!
                  </motion.span>
                ) : (
                  <motion.span
                    key="default"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="flex items-center gap-2"
                  >
                    {buttonLabel}
                    {ButtonIcon && <ButtonIcon size={18} />}
                  </motion.span>
                )}
              </AnimatePresence>
            </Button>

            {hasMultiplePlans && !onChoosePlan && data.pricingOptions && (
              <PlanSelector
                open={planSelectorOpen}
                onOpenChange={setPlanSelectorOpen}
                options={data.pricingOptions}
                productName={data.name}
                productId={id}
                coachName={data.ownerName ?? ""}
                coachUID={data.ownerUID ?? ""}
                coverArtURL={data.coverArtURL ?? undefined}
                stripeAccountID={data.stripeAccountID}
                isLemonadeProduct={data.isLemonadeProduct}
                boxID={data.boxID}
                onSuccess={showSuccessState}
                brandColor={brandColor}
              />
            )}
          </CardContent>
        )}
      </Card>
    </motion.div>
  );
}

export default ProductCard;
